<template>
    <div ref="rootElement" class="valuations-map-container">
        <div class="map-wrapper">
            <LMap :center="centerLatLng"
                  :zoom="zoom"
                  :options="mapOptions"
                  class="valuations-map"
                  ref="map"
                  @update:zoom="updateZoom"
                  @update:bounds="handleBoundsChange"
                  @click="handleMapClick"
                  @ready="onMapReady">
                <LControlLayers position="topright"></LControlLayers>
                <MapyCzBasicLayer/>
                <MapyCzOrthoPhotoLayer :visible="false"/>
                <LControlZoom position="topleft"/>
            </LMap>
            <div v-if="loading" class="map-spinner-control">
                <div class="spinner-border spinner-border-sm" role="status"></div>
            </div>
            <div class="map-top-panel">
                <div class="map-control d-flex justify-content-center">
                    <div class="map-clickable d-flex gap-3">
                        <EstateTypeControl :estateTypes="estateTypes" @change="estateTypeChange"/>
                        <DateRangeControl @change="dateRangeChange" :defaultDateRange="dateRange"/>
                    </div>
                </div>
            </div>
            <LeftPanel :visible="activeMarker !== null">
                <div class="h-100 d-flex flex-column">
                    <button class="btn btn-lg btn-link align-self-end" @click="closePanel">
                        <i class="fas fa-times"></i>
                    </button>
                    <div class="flex-grow-1 overflow-auto">
                        <ul class="list-group list-group-flush" v-if="activeMarker !== null">
                            <li v-for="valuation in sortedValuations"
                                :key="valuation.id"
                                class="list-group-item valuation-item">
                                <div class="d-flex align-items-start">
                                    <div v-if="valuation.image" class="valuation-image me-3">
                                        <img :src="valuation.image" :alt="valuation.title" class="rounded"/>
                                    </div>
                                    <div class="valuation-info flex-grow-1">
                                        <span class="badge mb-1" :style="estateTypeBadgeStyle(valuation.estateType)">
                                            {{ getEstateTypeLabel(valuation.estateType) }}
                                        </span>
                                        <div class="valuation-title">{{ valuation.title }}</div>
                                        <table class="valuation-details small"><tbody>
                                            <tr>
                                                <td class="text-muted pe-2">Účel</td>
                                                <td>{{ getPurposeOfValuationLabel(valuation.purposeOfValuation) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-muted pe-2">Odhadce</td>
                                                <td>{{ valuation.userName }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-muted pe-2">Vytvořeno</td>
                                                <td>{{ formatDate(valuation.createdAt) }}</td>
                                            </tr>
                                            <tr v-if="valuation.updatedAt">
                                                <td class="text-muted pe-2">Změněno</td>
                                                <td>{{ formatDate(valuation.updatedAt) }}</td>
                                            </tr>
                                            <tr>
                                                <td class="text-muted pe-2">Oceněno</td>
                                                <td>{{ formatDate(valuation.valuationDate) }}</td>
                                            </tr>
                                        </tbody></table>
                                        <div class="valuation-price-section mt-2">
                                            <template v-if="getValuationDetail(valuation.id)">
                                                <div v-if="getValuationDetail(valuation.id).price" class="valuation-price">
                                                    <span class="valuation-price-label">Cenový odhad</span>
                                                    {{ formatPrice(getValuationDetail(valuation.id).price) }}
                                                </div>
                                                <div v-if="getValuationDetail(valuation.id).share" class="valuation-share text-muted">
                                                    Podíl: {{ getValuationDetail(valuation.id).share.formatted }}
                                                </div>
                                            </template>
                                            <div v-else-if="isLoadingDetail(valuation.id)" class="text-muted">
                                                <div class="spinner-border spinner-border-sm" role="status">
                                                    <span class="visually-hidden">Načítání...</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="text-end mt-2">
                                            <a :href="getDetailPageUrl(valuation.id)" target="_blank" class="valuation-detail-link">
                                                Zobrazit detail <span class="fas fa-angle-right ms-1 estimate-detail-link-icon"></span>
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </li>
                        </ul>
                    </div>
                </div>
            </LeftPanel>
            <div v-if="zoomTooSmall" class="zoom-prompt-overlay">
                <div class="zoom-prompt-message">
                    Pro zobrazení odhadů přibližte mapu
                </div>
            </div>
            <div class="map-legend">
                <div class="map-legend-content">
                    <div v-for="type in allEstateTypes"
                         :key="type"
                         class="legend-item">
                        <span class="legend-marker" :style="{ backgroundColor: getEstateTypeColor(type) }"></span>
                        <span class="legend-label">{{ getEstateTypeLabel(type) }}</span>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script lang="ts">
import DateRangeControl, {DateRange} from '@/components/common/DateRangeControl.vue';
import EstateTypeControl from '@/components/common/EstateTypeControl.vue';
import LeftPanel from '@/components/maps/LeftPanel.vue';
import MapyCzBasicLayer from '@/components/maps/MapyCzBasicLayer.vue';
import MapyCzOrthoPhotoLayer from '@/components/maps/MapyCzOrtophotoLayer.vue';
import {EstateType} from '@/model/api';
import {log} from '@/model/logger';
import {DivIcon} from 'leaflet';
import L from 'leaflet';
import {computed, defineComponent, nextTick, onBeforeUnmount, onMounted, PropType, ref} from 'vue';
import {useI18n} from 'vue-i18n';
import {LControlLayers, LControlZoom, LMap} from '@vue-leaflet/vue-leaflet';

interface Coordinates {
    readonly latitude: number;
    readonly longitude: number;
}

interface BoundingBox {
    readonly southWest: Coordinates;
    readonly northEast: Coordinates;
}

type ValuationEstateType = 'flat' | 'family_house' | 'land' | 'garage' | 'recreational' | 'commercial' | 'apartment_house';

interface Valuation {
    readonly id: string;
    readonly coordinates: Coordinates;
    readonly estateType: ValuationEstateType;
    readonly title: string;
    readonly image: string | null;
    readonly valuationDate: string;
    readonly purposeOfValuation: string;
    readonly userName: string | null;
    readonly createdAt: string;
    readonly updatedAt: string | null;
    readonly detailUrl?: string;
}

interface ValuationDetail {
    readonly price: {
        readonly amount: number;
        readonly currency: string;
    } | null;
    readonly share: {
        readonly numerator: number;
        readonly denominator: number;
        readonly formatted: string;
    } | null;
}

interface Marker {
    readonly coordinates: L.LatLngTuple;
    readonly valuations: Valuation[];
}

interface ValuationsRequest {
    readonly boundingBox: BoundingBox;
    readonly estateType: EstateType | null;
    readonly dateRange: {
        readonly start: string;
        readonly end: string;
    };
}

const ID_PLACEHOLDER = '00000000-0000-0000-0000-000000000000';

const MIN_ZOOM = 10;
const DEFAULT_ZOOM = 11;
const GEOLOCATION_TIMEOUT = 5000;
const GEOLOCATION_MAX_AGE = 60000;
const FLY_TO_DURATION = 1;
const ACTIVE_MARKER_Z_OFFSET = 1000;
const BOUNDS_CHANGE_DEBOUNCE = 300;

const ESTATE_TYPE_COLORS: Record<ValuationEstateType, string> = {
    flat: '#0d6efd',
    family_house: '#198754',
    land: '#ffc107',
    garage: '#6c757d',
    recreational: '#0dcaf0',
    commercial: '#212529',
    apartment_house: '#9333ea',
};

export default defineComponent({
    components: {
        MapyCzOrthoPhotoLayer,
        MapyCzBasicLayer,
        LeftPanel,
        LMap,
        LControlLayers,
        LControlZoom,
        DateRangeControl,
        EstateTypeControl,
    },
    props: {
        center: {
            type: Object as PropType<Coordinates>,
            required: true,
        },
        loadValuationsUrl: {
            type: String,
            required: true,
        },
        loadDetailUrl: {
            type: String,
            required: true,
        },
        detailPageUrl: {
            type: String,
            required: true,
        },
        estateTypes: {
            type: Array as PropType<EstateType[]>,
            required: true,
        },
        defaultDateRange: {
            type: Object as PropType<DateRange>,
            required: true,
        },
        useGeolocation: {
            type: Boolean,
            default: true,
        },
    },
    setup(props) {
        const {t} = useI18n();
        const rootElement = ref<HTMLElement | null>(null);
        const mapRef = ref<any>(null);
        const leafletMap = ref<L.Map | null>(null);
        const mapReady = ref(false);
        const zoom = ref(DEFAULT_ZOOM);
        const loading = ref(false);
        const dateRange = ref<DateRange>({start: new Date(), end: new Date()});
        const estateType = ref<EstateType | null>(null);
        const activeMarker = ref<Marker | null>(null);
        const valuationDetails = ref<Record<string, ValuationDetail>>({});
        const loadingDetails = ref<Record<string, boolean>>({});

        let previousAbortController: AbortController | null = null;
        const valuationsCache: Map<string, Valuation> = new Map();
        let loadedBoundsAreas: L.LatLngBounds[] = [];
        let visibleMarkers: Map<string, [Marker, L.Marker]> = new Map();
        let activeLeafletMarker: L.Marker | null = null;
        let visibilityObserver: IntersectionObserver | null = null;
        let isAnimating = false;
        let boundsChangeTimeout: ReturnType<typeof setTimeout> | null = null;

        // Computed
        const centerLatLng = computed((): L.LatLng => {
            return L.latLng(props.center.latitude, props.center.longitude);
        });

        const zoomTooSmall = computed((): boolean => {
            return zoom.value < MIN_ZOOM;
        });

        const mapOptions = computed((): L.MapOptions => {
            return {
                zoomSnap: 0.25,
                preferCanvas: true,
                zoomControl: false,
            };
        });

        const sortedValuations = computed((): Valuation[] => {
            if (activeMarker.value === null) {
                return [];
            }

            return [...activeMarker.value.valuations].sort((a, b) => {
                return b.valuationDate.localeCompare(a.valuationDate);
            });
        });

        const allEstateTypes = computed((): ValuationEstateType[] => {
            return Object.keys(ESTATE_TYPE_COLORS) as ValuationEstateType[];
        });

        // Map events
        function updateZoom(newZoom: number): void {
            zoom.value = newZoom;
        }

        function handleBoundsChange(): void {
            if (isAnimating) {
                return;
            }

            if (boundsChangeTimeout) {
                clearTimeout(boundsChangeTimeout);
            }

            boundsChangeTimeout = setTimeout(() => {
                loadValuationsDebounced();
            }, BOUNDS_CHANGE_DEBOUNCE);
        }

        function dateRangeChange(newDateRange: DateRange): void {
            clearCache();
            dateRange.value = newDateRange;
            handleBoundsChange();
        }

        function estateTypeChange(newEstateType: EstateType | null): void {
            clearCache();
            estateType.value = newEstateType;
            handleBoundsChange();
        }

        function closePanel(): void {
            deactivateMarker();
        }

        function handleMapClick(): void {
            deactivateMarker();
        }

        function getEstateTypeLabel(et: ValuationEstateType): string {
            return t(`parameters.estateType.options.${et}`).toString();
        }

        function getEstateTypeColor(et: ValuationEstateType): string {
            return ESTATE_TYPE_COLORS[et] ?? '#6c757d';
        }

        function getPurposeOfValuationLabel(purposeOfValuation: string): string {
            return t(`parameters.purposeOfValuation.options.${purposeOfValuation}`).toString();
        }

        function formatDate(isoDate: string): string {
            const date = new Date(isoDate);

            return new Intl.DateTimeFormat('cs-CZ', {
                day: 'numeric',
                month: 'numeric',
                year: 'numeric',
            }).format(date);
        }

        function estateTypeBadgeStyle(et: ValuationEstateType): Record<string, string> {
            const color = ESTATE_TYPE_COLORS[et] ?? '#6c757d';
            const needsDarkText = et === 'land';

            return {
                backgroundColor: color,
                color: needsDarkText ? '#212529' : '#ffffff',
            };
        }

        function getValuationDetail(valuationId: string): ValuationDetail | null {
            return valuationDetails.value[valuationId] ?? null;
        }

        function isLoadingDetail(valuationId: string): boolean {
            return !!loadingDetails.value[valuationId];
        }

        function formatPrice(price: { amount: number; currency: string }): string {
            return new Intl.NumberFormat('cs-CZ', {
                style: 'currency',
                currency: price.currency,
                minimumFractionDigits: 0,
                maximumFractionDigits: 0,
            }).format(price.amount);
        }

        function getDetailPageUrl(valuationId: string): string {
            return props.detailPageUrl.replace(ID_PLACEHOLDER, valuationId);
        }

        async function loadValuationDetail(valuationId: string): Promise<void> {
            if (loadingDetails.value[valuationId] || valuationId in valuationDetails.value) {
                return;
            }

            loadingDetails.value = {...loadingDetails.value, [valuationId]: true};

            try {
                const url = props.loadDetailUrl.replace(ID_PLACEHOLDER, valuationId);
                const response = await fetch(url);

                if (!response.ok) {
                    throw new Error(`Failed to load detail: ${response.status}`);
                }

                const detail: ValuationDetail = await response.json();
                valuationDetails.value = {...valuationDetails.value, [valuationId]: detail};
            } catch (error) {
                log(error instanceof Error ? error.message : String(error));
            } finally {
                const newLoadingDetails = {...loadingDetails.value};
                delete newLoadingDetails[valuationId];
                loadingDetails.value = newLoadingDetails;
            }
        }

        // Formatting
        function parseDateRange(dr: DateRange): DateRange {
            return {
                start: dr.start instanceof Date ? dr.start : new Date(dr.start),
                end: dr.end instanceof Date ? dr.end : new Date(dr.end),
            };
        }

        function formatDateForApi(date: Date): string {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');

            return `${year}-${month}-${day}`;
        }

        // Marker state
        function resetActiveMarkerStyle(): void {
            if (activeLeafletMarker && activeMarker.value) {
                activeLeafletMarker.setIcon(createMarkerIcon(activeMarker.value, false));
                activeLeafletMarker.setZIndexOffset(0);
            }
        }

        function deactivateMarker(): void {
            resetActiveMarkerStyle();
            activeMarker.value = null;
            activeLeafletMarker = null;
        }

        function onMapReady(mapInstance: L.Map): void {
            leafletMap.value = mapInstance;
            mapReady.value = true;
        }

        // Visibility
        function waitForVisibility(): void {
            const element = rootElement.value!;

            visibilityObserver = new IntersectionObserver((entries) => {
                entries.forEach((entry) => {
                    if (entry.isIntersecting) {
                        nextTick(() => {
                            if (mapReady.value && leafletMap.value) {
                                leafletMap.value.invalidateSize();
                            }

                            setTimeout(() => {
                                tryUseUserLocation();
                            }, 100);

                            handleBoundsChange();
                        });

                        if (visibilityObserver) {
                            visibilityObserver.disconnect();
                            visibilityObserver = null;
                        }
                    }
                });
            }, {
                threshold: 0.1,
            });

            visibilityObserver.observe(element);
        }

        // User location
        function tryUseUserLocation(): void {
            if (!props.useGeolocation) {
                return;
            }

            if (!navigator.geolocation) {
                return;
            }

            navigator.geolocation.getCurrentPosition(
                (position) => handleGeolocationSuccess(position),
                (error) => handleGeolocationError(error),
                {
                    timeout: GEOLOCATION_TIMEOUT,
                    maximumAge: GEOLOCATION_MAX_AGE,
                },
            );
        }

        function handleGeolocationSuccess(position: GeolocationPosition): void {
            const latitude = position.coords.latitude;
            const longitude = position.coords.longitude;

            if (!isFinite(latitude) || !isFinite(longitude)) {
                log('Invalid geolocation coordinates:', {latitude, longitude});
                return;
            }

            const userLocation = L.latLng(latitude, longitude);

            if (!leafletMap.value) {
                log('Map not ready for flyTo');
                return;
            }

            isAnimating = true;

            const onMoveEnd = () => {
                isAnimating = false;
                leafletMap.value!.off('moveend', onMoveEnd);
                handleBoundsChange();
            };

            leafletMap.value.on('moveend', onMoveEnd);

            leafletMap.value.flyTo(userLocation, DEFAULT_ZOOM, {
                duration: FLY_TO_DURATION,
            });
        }

        function handleGeolocationError(error: GeolocationPositionError): void {
            log('Geolocation error:', error.message);
        }

        // Data loading
        async function loadValuationsDebounced(): Promise<void> {
            const abortController = new AbortController();

            try {
                loading.value = true;
                abortPreviousRequest();
                previousAbortController = abortController;

                await refreshValuations(abortController.signal);
            } catch (error) {
                if (!abortController.signal.aborted) {
                    log(error instanceof Error ? error.message : String(error));
                }
            } finally {
                if (!abortController.signal.aborted) {
                    loading.value = false;
                }
            }
        }

        function abortPreviousRequest(): void {
            previousAbortController?.abort();
        }

        function clearCache(): void {
            valuationsCache.clear();
            loadedBoundsAreas = [];
            removeAllMarkers();
            activeMarker.value = null;
            activeLeafletMarker = null;
            valuationDetails.value = {};
            loadingDetails.value = {};
        }

        async function refreshValuations(signal: AbortSignal): Promise<void> {
            if (zoomTooSmall.value) {
                removeAllMarkers();
                return;
            }

            if (!leafletMap.value) {
                return;
            }

            const currentBounds = leafletMap.value.getBounds();

            if (!currentBounds || !currentBounds.isValid()) {
                return;
            }

            if (isAreaLoaded(currentBounds)) {
                updateVisibleMarkers();
                return;
            }

            const valuations = await fetchValuations(currentBounds, signal);
            addValuationsToCache(valuations);
            expandLoadedBounds(currentBounds);
            updateVisibleMarkers();
        }

        function isAreaLoaded(bounds: L.LatLngBounds): boolean {
            if (loadedBoundsAreas.length === 0) {
                return false;
            }

            return loadedBoundsAreas.some((loadedBounds) => loadedBounds.contains(bounds));
        }

        function expandLoadedBounds(newBounds: L.LatLngBounds): void {
            const newBoundsObj = L.latLngBounds(
                newBounds.getSouthWest(),
                newBounds.getNorthEast(),
            );

            let merged = false;
            for (const existingBounds of loadedBoundsAreas) {
                if (boundsSignificantlyOverlap(existingBounds, newBoundsObj)) {
                    existingBounds.extend(newBoundsObj);
                    merged = true;
                    break;
                }
            }

            if (!merged) {
                loadedBoundsAreas.push(newBoundsObj);
            }

            mergeOverlappingBounds();
        }

        function boundsSignificantlyOverlap(bounds1: L.LatLngBounds, bounds2: L.LatLngBounds): boolean {
            if (!bounds1.intersects(bounds2)) {
                return false;
            }

            const intersection = L.latLngBounds(
                L.latLng(
                    Math.max(bounds1.getSouth(), bounds2.getSouth()),
                    Math.max(bounds1.getWest(), bounds2.getWest()),
                ),
                L.latLng(
                    Math.min(bounds1.getNorth(), bounds2.getNorth()),
                    Math.min(bounds1.getEast(), bounds2.getEast()),
                ),
            );

            const intersectionArea = getBoundsArea(intersection);
            const smallerArea = Math.min(getBoundsArea(bounds1), getBoundsArea(bounds2));

            return intersectionArea >= smallerArea * 0.3;
        }

        function getBoundsArea(bounds: L.LatLngBounds): number {
            const width = bounds.getEast() - bounds.getWest();
            const height = bounds.getNorth() - bounds.getSouth();
            return width * height;
        }

        function mergeOverlappingBounds(): void {
            let merged = true;
            while (merged) {
                merged = false;
                for (let i = 0; i < loadedBoundsAreas.length; i++) {
                    for (let j = i + 1; j < loadedBoundsAreas.length; j++) {
                        if (boundsSignificantlyOverlap(loadedBoundsAreas[i], loadedBoundsAreas[j])) {
                            loadedBoundsAreas[i].extend(loadedBoundsAreas[j]);
                            loadedBoundsAreas.splice(j, 1);
                            merged = true;
                            break;
                        }
                    }
                    if (merged) {
                        break;
                    }
                }
            }
        }

        function addValuationsToCache(valuations: Valuation[]): void {
            valuations.forEach((valuation) => {
                valuationsCache.set(valuation.id, valuation);
            });
        }

        async function fetchValuations(bounds: L.LatLngBounds, signal: AbortSignal): Promise<Valuation[]> {
            const requestData = buildRequestData(bounds);
            const response = await sendRequest(requestData, signal);

            try {
                return await response.json();
            } catch {
                log('Failed to parse valuations response');
                return [];
            }
        }

        function buildRequestData(bounds: L.LatLngBounds): ValuationsRequest {
            return {
                boundingBox: {
                    southWest: {
                        longitude: bounds.getSouthWest().lng,
                        latitude: bounds.getSouthWest().lat,
                    },
                    northEast: {
                        longitude: bounds.getNorthEast().lng,
                        latitude: bounds.getNorthEast().lat,
                    },
                },
                estateType: estateType.value,
                dateRange: {
                    start: formatDateForApi(dateRange.value.start),
                    end: formatDateForApi(dateRange.value.end),
                },
            };
        }

        async function sendRequest(requestData: ValuationsRequest, signal: AbortSignal): Promise<Response> {
            const response = await fetch(props.loadValuationsUrl, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(requestData),
                signal,
            });

            if (!response.ok) {
                throw new Error(`Valuations refresh failed with status ${response.status}`);
            }

            return response;
        }

        // Markers
        function removeAllMarkers(): void {
            visibleMarkers.forEach(([, leafletMarker]) => {
                leafletMarker.remove();
            });
            visibleMarkers.clear();
        }

        function updateVisibleMarkers(): void {
            const markers = combineToMarkers(Array.from(valuationsCache.values()));
            const newVisibleMarkers = new Map<string, [Marker, L.Marker]>();
            const mapObj = leafletMap.value;

            if (!mapObj) {
                return;
            }

            for (const [key, marker] of markers.entries()) {
                const existingPair = visibleMarkers.get(key);

                let leafletMarker: L.Marker;

                if (existingPair) {
                    leafletMarker = existingPair[1];
                    const isActive = isMarkerActive(key);
                    leafletMarker.setIcon(createMarkerIcon(marker, isActive));
                    visibleMarkers.delete(key);
                } else {
                    leafletMarker = L.marker(marker.coordinates, {
                        icon: createMarkerIcon(marker),
                    });
                    leafletMarker.on('click', () => markerClicked(marker, leafletMarker));
                    leafletMarker.addTo(mapObj as L.Map);
                }

                newVisibleMarkers.set(key, [marker, leafletMarker]);
            }

            visibleMarkers.forEach(([, leafletMarker]) => {
                leafletMarker.remove();
            });

            visibleMarkers = newVisibleMarkers;
        }

        function combineToMarkers(valuations: Valuation[]): Map<string, Marker> {
            const markers = new Map<string, Marker>();

            valuations.forEach((valuation) => {
                const coordinates: L.LatLngTuple = [valuation.coordinates.latitude, valuation.coordinates.longitude];
                const key = coordinatesKey(coordinates);
                const existingMarker = markers.get(key);

                if (!existingMarker) {
                    markers.set(key, {
                        coordinates,
                        valuations: [valuation],
                    });
                } else {
                    markers.set(key, {
                        ...existingMarker,
                        valuations: [...existingMarker.valuations, valuation],
                    });
                }
            });

            return markers;
        }

        function coordinatesKey(coordinates: L.LatLngTuple): string {
            return `${coordinates[0]}_${coordinates[1]}`;
        }

        function isMarkerActive(key: string): boolean {
            if (!activeMarker.value) {
                return false;
            }
            return coordinatesKey(activeMarker.value.coordinates) === key;
        }

        function createMarkerIcon(marker: Marker, isActive: boolean = false): DivIcon {
            const count = marker.valuations.length;
            const content = count === 1 ? '' : `<span class="marker-count">${count}</span>`;
            const activeClass = isActive ? ' valuation-marker--active' : '';
            const backgroundStyle = getMarkerBackgroundStyle(marker);

            return new DivIcon({
                className: '',
                iconAnchor: [12, 24],
                popupAnchor: [0, -24],
                html: `<span class="valuation-marker${activeClass}" style="${backgroundStyle}">${content}</span>`,
            });
        }

        function getMarkerBackgroundStyle(marker: Marker): string {
            const types = getUniqueEstateTypes(marker);

            if (types.length === 1) {
                const color = ESTATE_TYPE_COLORS[types[0]];
                return `background-color: ${color};`;
            }

            return createConicGradient(types);
        }

        function getUniqueEstateTypes(marker: Marker): ValuationEstateType[] {
            const types = new Set<ValuationEstateType>();
            marker.valuations.forEach((v) => types.add(v.estateType));
            return Array.from(types);
        }

        function createConicGradient(types: ValuationEstateType[]): string {
            const segmentSize = 360 / types.length;
            const gradientStops: string[] = [];

            types.forEach((type, index) => {
                const color = ESTATE_TYPE_COLORS[type];
                const startAngle = index * segmentSize;
                const endAngle = (index + 1) * segmentSize;
                gradientStops.push(`${color} ${startAngle}deg ${endAngle}deg`);
            });

            return `background: conic-gradient(${gradientStops.join(', ')});`;
        }

        function markerClicked(marker: Marker, leafletMarker: L.Marker): void {
            resetActiveMarkerStyle();

            activeMarker.value = marker;
            activeLeafletMarker = leafletMarker;
            leafletMarker.setIcon(createMarkerIcon(marker, true));
            leafletMarker.setZIndexOffset(ACTIVE_MARKER_Z_OFFSET);

            for (const valuation of marker.valuations) {
                loadValuationDetail(valuation.id);
            }
        }

        // Lifecycle: created
        dateRange.value = parseDateRange(props.defaultDateRange);

        onMounted(() => {
            waitForVisibility();
        });

        onBeforeUnmount(() => {
            previousAbortController?.abort();

            if (visibilityObserver) {
                visibilityObserver.disconnect();
            }
            if (boundsChangeTimeout) {
                clearTimeout(boundsChangeTimeout);
            }
        });

        return {
            map: mapRef,
            zoom,
            loading,
            dateRange,
            activeMarker,
            centerLatLng,
            zoomTooSmall,
            mapOptions,
            sortedValuations,
            allEstateTypes,
            updateZoom,
            handleBoundsChange,
            handleMapClick,
            dateRangeChange,
            estateTypeChange,
            closePanel,
            getEstateTypeLabel,
            getEstateTypeColor,
            getPurposeOfValuationLabel,
            formatDate,
            estateTypeBadgeStyle,
            getValuationDetail,
            isLoadingDetail,
            formatPrice,
            getDetailPageUrl,
            rootElement,
            onMapReady,
        };
    },
});
</script>

<style scoped>
.valuations-map-container {
    height: 100%;
    width: 100%;
}

.map-wrapper {
    position: relative;
    height: 100%;
    width: 100%;
    overflow: hidden;
}

.valuations-map {
    height: 100%;
    width: 100%;
}

.map-top-panel {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    z-index: 600;
    pointer-events: none;
}

.map-clickable {
    pointer-events: auto;
}

.map-control {
    padding: 12px;
}

.map-panel {
    z-index: 1001;
}

.map-spinner-control {
    position: absolute;
    bottom: 10px;
    left: 10px;
    width: 32px;
    height: 32px;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: white;
    box-shadow: 0 1px 5px rgba(0, 0, 0, 0.4);
    border-radius: 4px;
    pointer-events: none;
    z-index: 1100;
}

.zoom-prompt-overlay {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.85);
    z-index: 900;
    pointer-events: none;
}

.zoom-prompt-message {
    background-color: white;
    padding: 10px 16px;
    border-radius: 6px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
    font-size: 13px;
    font-weight: 500;
    color: #374151;
    text-align: center;
    white-space: nowrap;
}

.valuation-item {
    padding: 12px;
}

.valuation-item a {
    color: inherit;
}

.valuation-item:hover {
    background-color: #f8f9fa;
}

.valuation-image {
    width: 60px;
    height: 60px;
    flex-shrink: 0;
}

.valuation-image img {
    width: 100%;
    height: 100%;
    object-fit: cover;
}

.valuation-info {
    min-width: 0;
}

.valuation-title {
    font-size: 14px;
    color: #333;
    word-break: break-word;
}

.valuation-details {
    margin-top: 8px;
    border-collapse: collapse;
}

.valuation-details td {
    padding: 2px 0;
    vertical-align: top;
}

.valuation-details td:first-child {
    white-space: nowrap;
    font-weight: 500;
}

.valuation-price-section {
    border-top: 1px solid #dee2e6;
    padding-top: 8px;
}

.valuation-price {
    font-size: 15px;
    font-weight: 600;
    color: #212529;
}

.valuation-price-label {
    font-weight: 400;
    color: #6c757d;
    margin-right: 4px;
}

.valuation-share {
    font-size: 13px;
}

.valuation-detail-link {
    font-size: 13px;
    text-decoration: none;
}

.valuation-detail-link:hover {
    text-decoration: underline;
}

.estimate-detail-link-icon {
    position: relative;
    top: 1px;
}

.map-legend {
    position: absolute;
    bottom: 8px;
    left: 50%;
    transform: translateX(-50%);
    z-index: 600;
    pointer-events: none;
}

.map-legend-content {
    display: flex;
    flex-wrap: wrap;
    gap: 8px 16px;
    justify-content: center;
    background-color: rgba(255, 255, 255, 0.95);
    padding: 6px 12px;
    border-radius: 6px;
    box-shadow: 0 1px 4px rgba(0, 0, 0, 0.15);
}

.legend-item {
    display: flex;
    align-items: center;
    gap: 5px;
}

.legend-marker {
    width: 12px;
    height: 12px;
    border-radius: 50%;
    border: 1px solid white;
    box-shadow: 0 1px 2px rgba(0, 0, 0, 0.2);
    flex-shrink: 0;
}

.legend-label {
    font-size: 11px;
    color: #374151;
    white-space: nowrap;
}
</style>

<style>
.valuation-marker {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 24px;
    height: 24px;
    background-color: #9333ea;
    border: 2px solid white;
    border-radius: 50%;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.3);
    color: white;
    font-size: 11px;
    font-weight: bold;
    transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.valuation-marker:hover {
    transform: scale(1.1);
    cursor: pointer;
}

.valuation-marker--active {
    transform: scale(1.3);
    box-shadow: 0 0 0 4px rgba(255, 255, 255, 0.9), 0 0 0 6px rgba(0, 0, 0, 0.6), 0 4px 12px rgba(0, 0, 0, 0.5);
    border-color: #ffffff;
    border-width: 3px;
}

.valuation-marker--active:hover {
    transform: scale(1.3);
}

.valuation-marker .marker-count {
    background-color: rgba(0, 0, 0, 0.3);
    border-radius: 50%;
    width: 16px;
    height: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    font-size: 10px;
}
</style>
